import React, { useState, useRef, useEffect } from 'react';
import { TextField, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addAdCategory, updateAdCategory } from '../../utils/api';

const AdCategoryForm = ({ editCategory = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const isEditMode = !!editCategory;
  const onSuccessTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    },
    []
  );

  const getCategoryId = (category) =>
    category?.categoryId ??
    category?.id;

  const getCategoryName = (category) =>
    category?.categoryName ??
    category?.name ??
    '';

  const validationSchema = Yup.object({
    categoryName: Yup.string()
      .required('Category name is required')
      .max(100, 'Category name must be at most 100 characters'),
    description: Yup.string()
      .required('Description is required')
      .max(500, 'Description must be at most 500 characters'),
    price: Yup.number()
      .typeError('Price must be a number')
      .required('Price is required')
      .min(0, 'Price must be at least 0'),
  });

  const formik = useFormik({
    initialValues: {
      categoryName: editCategory ? getCategoryName(editCategory) : '',
      description: editCategory?.description ?? '',
      price: editCategory?.price ?? '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        if (isEditMode) {
          const categoryId = getCategoryId(editCategory);
          await updateAdCategory(categoryId, values, {
            previousData: editCategory,
            identifier: getCategoryName(editCategory)
          });
          setSuccessMessage('Ads category updated successfully!');
        } else {
          await addAdCategory(values, {
            identifier: values.categoryName
          });
          setSuccessMessage('Ads category added successfully!');
          resetForm();
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} ads category:`, error);
        setErrorMessage(
          error.response?.data?.message ||
          `Failed to ${isEditMode ? 'update' : 'add'} ads category. Please try again.`
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return (
    <FormContainer
      title={isEditMode ? 'Edit Ads Category' : 'Add Ads Category'}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="categoryName"
            name="categoryName"
            label="Category Name"
            value={formik.values.categoryName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.categoryName && Boolean(formik.errors.categoryName)}
            helperText={formik.touched.categoryName && formik.errors.categoryName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="price"
            name="price"
            label="Price"
            type="number"
            value={formik.values.price}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.price && Boolean(formik.errors.price)}
            helperText={formik.touched.price && formik.errors.price}
            margin="normal"
            variant="outlined"
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default AdCategoryForm;

